import { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * A masked field you can unmask to check what you typed.
 *
 * Used for passwords, and for the bank and Aadhaar numbers that are masked for
 * the same shoulder-surfing reason. Those matter most: nobody can retype a
 * twelve-digit account number from memory to verify it, which is why each has a
 * "confirm" twin — and why being able to look is worth more there than on a
 * password somebody knows by heart.
 *
 * One component rather than a toggle written nine times. Nine hand-rolled
 * copies is how a codebase ends up with three that behave differently and one
 * that submits the form when you click the eye.
 */
export function MaskedInput({
  id,
  className = 'input',
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & { id?: string }) {
  const [revealed, setRevealed] = useState(false);
  const fallbackId = useId();
  const inputId = id ?? fallbackId;

  return (
    <div className="relative">
      <input
        {...props}
        id={inputId}
        type={revealed ? 'text' : 'password'}
        // Room for the button, so a long value never runs underneath it.
        className={`${className} pr-10`}
      />
      <button
        // Without type="button" this submits the form it sits in — the default
        // for a button inside a <form> is submit.
        type="button"
        onClick={() => setRevealed((v) => !v)}
        // The state has to be spoken, not just drawn: an icon that swaps between
        // two outlines tells a screen reader nothing.
        aria-label={revealed ? 'Hide' : 'Show'}
        aria-pressed={revealed}
        aria-controls={inputId}
        // Skipped in tab order. Someone filling the form should reach the next
        // field, not a display toggle.
        tabIndex={-1}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400
                   transition-colors hover:text-gray-600 focus:outline-none
                   focus-visible:text-gray-700"
      >
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
